import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Community } from '../../services/communityService';
import { useCommunity } from '../../contexts/CommunityContext';

interface Props {
  open: boolean;
  community: Community;
  onClose: () => void;
  onSaved?: () => void;
}

export const EditCommunityModal: React.FC<Props> = ({
  open,
  community,
  onClose,
  onSaved,
}) => {
  const { updateCommunityDetails } = useCommunity();
  const [name, setName] = useState(community.name);
  const [description, setDescription] = useState(community.description || '');
  const [visibility, setVisibility] = useState<'public' | 'private' | 'invite_only'>(
    community.visibility
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setName(community.name);
      setDescription(community.description || '');
      setVisibility(community.visibility);
      setError(null);
    }
  }, [open, community]);

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      setError('Name must be at least 2 characters.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await updateCommunityDetails(community.community_id, {
        name: trimmed,
        description: description.trim() || undefined,
        visibility,
      });
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message || 'Failed to update community.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Edit community
        <IconButton onClick={handleClose} size="small" disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            label="Community name"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            disabled={loading}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            disabled={loading}
          />
          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel component="legend">Visibility</FormLabel>
            <RadioGroup
              value={visibility}
              onChange={(e) =>
                setVisibility(e.target.value as 'public' | 'private' | 'invite_only')
              }
            >
              <FormControlLabel
                value="public"
                control={<Radio />}
                label="Public — discoverable in search"
                disabled={loading}
              />
              <FormControlLabel
                value="private"
                control={<Radio />}
                label="Private — invite link only"
                disabled={loading}
              />
              <FormControlLabel
                value="invite_only"
                control={<Radio />}
                label="Invite only"
                disabled={loading}
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Box sx={{ position: 'relative' }}>
            <Button type="submit" variant="contained" disabled={loading}>
              Save changes
            </Button>
            {loading && (
              <CircularProgress size={22} sx={{ position: 'absolute', top: 8, right: 8 }} />
            )}
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};
